import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, FormControlLabel, IconButton, Switch, TextField, Tooltip, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { useMountEffect } from '../../utils/hooks';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const intersects = (a, b) => (
  a.x < b.x + b.width
  && a.x + a.width > b.x
  && a.y < b.y + b.height
  && a.y + a.height > b.y
);

const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const getWaypointKey = (waypoint, index) => (
  waypoint?.Id || waypoint?.id || waypoint?.WaypointName || `waypoint-${index}`
);

export default function InteractiveRobotMapCanvas({
  mapImage,
  waypoints = [],
  robotStatus = {},
  laserPoints = [],
  robotName = 'Robot',
  selectedWaypointName = '',
  onWaypointClick,
  onPointTargetClick,
  onPointTargetChange,
  routePoints = [],
  routeState = 'idle',
  targetLocked = false,
  missionStatus = '',
  missionTarget = null,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const missionLockLabel = missionStatus === 'PENDING'
    ? 'Mission scheduled'
    : missionStatus === 'PAUSED'
      ? 'Mission paused'
      : missionStatus === 'IN_PROGRESS'
        ? 'Robot en route'
        : 'Navigation locked';
  const imageRef = useRef(null);
  const imageLoadIdRef = useRef(0);
  const sizeRef = useRef({ width: 0, height: 0, dpr: 1 });
  const viewRef = useRef({
    scale: 1,
    fitScale: 1,
    rotation: 0,
    panX: 0,
    panY: 0,
  });
  const dataRef = useRef({});
  const hitTargetsRef = useRef([]);
  const hoveredWaypointRef = useRef(null);
  const temporaryPointTargetRef = useRef(null);
  const hadMissionTargetRef = useRef(false);
  const pointersRef = useRef(new Map());
  const dragRef = useRef(null);
  const gestureRef = useRef(null);
  const drawRef = useRef(() => {});
  const fitViewRef = useRef(() => {});
  const [hoveredMapPixel, setHoveredMapPixel] = useState(null);
  const [showRobot, setShowRobot] = useState(true);
  const [showWaypoints, setShowWaypoints] = useState(true);
  const [showAllWaypointNames, setShowAllWaypointNames] = useState(false);
  const [waypointSearch, setWaypointSearch] = useState('');
  const [pointTargetMode, setPointTargetMode] = useState(false);
  const [temporaryPointTarget, setTemporaryPointTarget] = useState(null);

  const updateTemporaryPointTarget = (target) => {
    temporaryPointTargetRef.current = target;
    dataRef.current.temporaryPointTarget = target;
    setTemporaryPointTarget(target);
    if (typeof dataRef.current.onPointTargetChange === 'function') {
      dataRef.current.onPointTargetChange(target);
    }
  };

  const clearTemporaryPointTarget = () => {
    updateTemporaryPointTarget(null);
    drawRef.current();
  };

  useEffect(() => {
    if (missionTarget) {
      const target = {
        x: Number(missionTarget.x),
        y: Number(missionTarget.y),
        z: Number(missionTarget.z || 0),
        angle: Number(missionTarget.heading ?? missionTarget.angle ?? 0),
      };
      if (!Number.isFinite(target.x) || !Number.isFinite(target.y)) return;
      hadMissionTargetRef.current = true;
      temporaryPointTargetRef.current = target;
      dataRef.current.temporaryPointTarget = target;
      setTemporaryPointTarget(target);
      setPointTargetMode(true);
      drawRef.current();
      return;
    }

    if (hadMissionTargetRef.current) {
      hadMissionTargetRef.current = false;
      temporaryPointTargetRef.current = null;
      dataRef.current.temporaryPointTarget = null;
      setTemporaryPointTarget(null);
      setPointTargetMode(false);
      drawRef.current();
    }
  }, [missionTarget]);

  dataRef.current = {
    waypoints,
    robotStatus,
    laserPoints,
    robotName,
    selectedWaypointName,
    showRobot,
    showWaypoints,
    showAllWaypointNames,
    waypointSearch,
    pointTargetMode,
    temporaryPointTarget,
    onWaypointClick,
    onPointTargetClick,
    onPointTargetChange,
    routePoints,
    routeState,
    targetLocked,
  };

  const mapToScreen = (x, y) => {
    const image = imageRef.current;
    const { width, height } = sizeRef.current;
    const view = viewRef.current;
    if (!image || !Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) {
      return null;
    }

    const localX = Number(x) - image.naturalWidth / 2;
    const localY = (image.naturalHeight - Number(y)) - image.naturalHeight / 2;
    const cos = Math.cos(view.rotation);
    const sin = Math.sin(view.rotation);

    return {
      x: width / 2 + view.panX + (localX * cos - localY * sin) * view.scale,
      y: height / 2 + view.panY + (localX * sin + localY * cos) * view.scale,
    };
  };

  const screenToMap = (x, y, roundToPixel = true) => {
    const image = imageRef.current;
    const { width, height } = sizeRef.current;
    const view = viewRef.current;
    if (!image || !width || !height || !view.scale) {
      return null;
    }

    const transformedX = (x - width / 2 - view.panX) / view.scale;
    const transformedY = (y - height / 2 - view.panY) / view.scale;
    const cos = Math.cos(view.rotation);
    const sin = Math.sin(view.rotation);
    const localX = transformedX * cos + transformedY * sin;
    const localY = -transformedX * sin + transformedY * cos;
    const mapX = localX + image.naturalWidth / 2;
    const mapY = image.naturalHeight / 2 - localY;

    if (
      mapX < 0
      || mapX >= image.naturalWidth
      || mapY < 0
      || mapY >= image.naturalHeight
    ) {
      return null;
    }

    return {
      x: roundToPixel ? Math.floor(mapX) : mapX,
      y: roundToPixel ? Math.floor(mapY) : mapY,
    };
  };

  drawRef.current = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    const { width, height, dpr } = sizeRef.current;
    if (!canvas || !width || !height) return;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, width, height);

    if (!image) {
      ctx.fillStyle = '#9ca3af';
      ctx.font = '600 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(mapImage ? 'Loading map…' : 'No map image available', width / 2, height / 2);
      hitTargetsRef.current = [];
      return;
    }

    const view = viewRef.current;
    ctx.save();
    ctx.translate(width / 2 + view.panX, height / 2 + view.panY);
    ctx.rotate(view.rotation);
    ctx.scale(view.scale, view.scale);
    ctx.translate(-image.naturalWidth / 2, -image.naturalHeight / 2);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0);
    ctx.restore();

    const {
      waypoints: currentWaypoints,
      robotStatus: currentRobotStatus,
      laserPoints: currentLaserPoints,
      robotName: currentRobotName,
      selectedWaypointName: currentSelectedWaypointName,
      showRobot: showCurrentRobot,
      showWaypoints: showCurrentWaypoints,
      showAllWaypointNames: showCurrentWaypointNames,
      waypointSearch: currentWaypointSearch,
      temporaryPointTarget: currentPointTarget,
      routePoints: currentRoutePoints,
      routeState: currentRouteState,
    } = dataRef.current;

    (currentLaserPoints || []).forEach((point) => {
      const screen = mapToScreen(point?.x, point?.y);
      if (!screen) return;
      ctx.beginPath();
      ctx.arc(screen.x, screen.y, 1.6, 0, Math.PI * 2);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
    });

    if ((currentRoutePoints || []).length > 1) {
      const screenPoints = currentRoutePoints
        .map((point) => mapToScreen(point?.x, point?.y))
        .filter(Boolean);
      if (screenPoints.length > 1) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(screenPoints[0].x, screenPoints[0].y);
        screenPoints.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
        ctx.setLineDash([12, 8]);
        ctx.lineDashOffset = currentRouteState === 'scheduled'
          ? 0
          : -(performance.now() * 0.035);
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        const routeColor = currentRouteState === 'error'
          ? '#ef4444'
          : currentRouteState === 'paused'
            ? '#f59e0b'
            : currentRouteState === 'travelling'
              ? '#22c55e'
              : '#22d3ee';
        ctx.strokeStyle = routeColor;
        ctx.shadowColor = routeColor;
        ctx.shadowBlur = 12;
        ctx.stroke();
        ctx.restore();
      }
    }
    const occupiedLabels = [];
    const hitTargets = [];
    const pulseProgress = (Math.sin(performance.now() / 220) + 1) / 2;
    const normalizedSearch = String(currentWaypointSearch || '').trim().toLowerCase();
    const orderedWaypoints = (showCurrentWaypoints ? currentWaypoints || [] : [])
      .map((waypoint, index) => ({ waypoint, index }))
      .filter(({ waypoint }) => (
        !normalizedSearch
        || String(waypoint?.WaypointName || '').toLowerCase().includes(normalizedSearch)
      ))
      .sort((a, b) => (
        Number(a.waypoint?.WaypointName === currentSelectedWaypointName)
        - Number(b.waypoint?.WaypointName === currentSelectedWaypointName)
      ));
    ctx.font = '700 12px Arial';

    orderedWaypoints.forEach(({ waypoint, index }) => {
      const position = mapToScreen(waypoint?.X, waypoint?.Y);
      if (!position) return;

      const name = waypoint?.WaypointName || `Waypoint ${index + 1}`;
      const key = getWaypointKey(waypoint, index);
      const hovered = hoveredWaypointRef.current === key;
      const selected = Boolean(currentSelectedWaypointName)
        && name === currentSelectedWaypointName;
      const showLabel = Boolean(showCurrentWaypointNames || hovered);
      let labelBounds = null;

      if (selected) {
        const pulseRadius = 13 + pulseProgress * 10;
        ctx.save();
        ctx.globalAlpha = 1 - pulseProgress * 0.55;
        ctx.shadowColor = '#f59e0b';
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.arc(position.x, position.y, pulseRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(245, 158, 11, 0.22)';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#fbbf24';
        ctx.stroke();
        ctx.restore();
      }

      if (showLabel) {
        const labelWidth = Math.max(58, ctx.measureText(name).width + 18);
        const labelHeight = 24;
        const gap = 12;
        const candidates = [
          { x: position.x + gap, y: position.y - labelHeight - 4 },
          { x: position.x + gap, y: position.y + 4 },
          { x: position.x - labelWidth - gap, y: position.y - labelHeight - 4 },
          { x: position.x - labelWidth - gap, y: position.y + 4 },
          { x: position.x - labelWidth / 2, y: position.y - labelHeight - 16 },
          { x: position.x - labelWidth / 2, y: position.y + 16 },
        ];

        let label = candidates.find((candidate) => {
          const padded = {
            x: candidate.x - 3,
            y: candidate.y - 3,
            width: labelWidth + 6,
            height: labelHeight + 6,
          };
          return (
            candidate.x >= 4
            && candidate.y >= 4
            && candidate.x + labelWidth <= width - 4
            && candidate.y + labelHeight <= height - 4
            && !occupiedLabels.some((existing) => intersects(padded, existing))
          );
        });

        if (!label) {
          const row = index % 6;
          label = {
            x: clamp(position.x + gap, 4, width - labelWidth - 4),
            y: clamp(position.y + 4 + row * (labelHeight + 3), 4, height - labelHeight - 4),
          };
        }

        labelBounds = {
          x: label.x,
          y: label.y,
          width: labelWidth,
          height: labelHeight,
        };
        occupiedLabels.push({
          x: label.x - 3,
          y: label.y - 3,
          width: labelWidth + 6,
          height: labelHeight + 6,
        });

        const labelAnchorX = clamp(position.x, label.x, label.x + labelWidth);
        const labelAnchorY = clamp(position.y, label.y, label.y + labelHeight);
        ctx.beginPath();
        ctx.moveTo(position.x, position.y);
        ctx.lineTo(labelAnchorX, labelAnchorY);
        ctx.strokeStyle = selected
          ? '#f59e0b'
          : (hovered ? '#fbbf24' : 'rgba(37, 99, 235, 0.8)');
        ctx.lineWidth = selected ? 3 : (hovered ? 2.5 : 1.5);
        ctx.stroke();

        ctx.save();
        ctx.shadowColor = 'rgba(15, 23, 42, 0.35)';
        ctx.shadowBlur = 6;
        drawRoundedRect(ctx, label.x, label.y, labelWidth, labelHeight, 6);
        ctx.fillStyle = selected
          ? '#d97706'
          : (hovered ? '#1d4ed8' : 'rgba(255, 255, 255, 0.96)');
        ctx.fill();
        ctx.lineWidth = selected ? 3 : 2;
        ctx.strokeStyle = selected ? '#ffffff' : (hovered ? '#fbbf24' : '#2563eb');
        ctx.stroke();
        ctx.restore();

        ctx.fillStyle = selected || hovered ? '#ffffff' : '#0f172a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, label.x + labelWidth / 2, label.y + labelHeight / 2);
      }

      ctx.beginPath();
      ctx.arc(
        position.x,
        position.y,
        selected ? 9 + pulseProgress * 2 : (hovered ? 8 : 5),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = selected ? '#f59e0b' : (hovered ? '#fbbf24' : '#2563eb');
      ctx.fill();
      ctx.lineWidth = selected ? 3 : 2;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();

      hitTargets.push({
        key,
        waypoint,
        marker: { x: position.x, y: position.y, radius: selected ? 20 : 12 },
        label: labelBounds,
      });
    });
    if (currentPointTarget) {
      const targetPosition = mapToScreen(currentPointTarget.x, currentPointTarget.y);
      if (targetPosition) {
        const targetHeading = Number.isFinite(Number(currentPointTarget.angle))
          ? Number(currentPointTarget.angle)
          : 0;
        const screenHeading = view.rotation - targetHeading;
        const directionX = Math.cos(screenHeading);
        const directionY = Math.sin(screenHeading);
        const ringRadius = 38;
        const handleX = targetPosition.x + directionX * ringRadius;
        const handleY = targetPosition.y + directionY * ringRadius;
        const pulseRadius = 12 + pulseProgress * 8;

        ctx.save();
        ctx.globalAlpha = 1 - pulseProgress * 0.45;
        ctx.shadowColor = '#22d3ee';
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(targetPosition.x, targetPosition.y, pulseRadius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(34, 211, 238, 0.25)';
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.setLineDash([6, 5]);
        ctx.beginPath();
        ctx.arc(targetPosition.x, targetPosition.y, ringRadius, 0, Math.PI * 2);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(8, 145, 178, 0.95)';
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(targetPosition.x, targetPosition.y);
        ctx.lineTo(handleX, handleY);
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.strokeStyle = '#0891b2';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(handleX, handleY, 8, 0, Math.PI * 2);
        ctx.fillStyle = '#22d3ee';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(targetPosition.x, targetPosition.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = '#06b6d4';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        ctx.restore();

        const targetLabel = `Target (${Math.round(currentPointTarget.x)}, ${Math.round(currentPointTarget.y)}) · ${Math.round((targetHeading * 180) / Math.PI)}°`;
        ctx.font = '700 12px Arial';
        const targetLabelWidth = ctx.measureText(targetLabel).width + 18;
        const targetLabelX = clamp(
          targetPosition.x - targetLabelWidth / 2,
          4,
          width - targetLabelWidth - 4
        );
        const targetLabelY = clamp(targetPosition.y + 48, 4, height - 28);
        drawRoundedRect(ctx, targetLabelX, targetLabelY, targetLabelWidth, 24, 6);
        ctx.fillStyle = 'rgba(8, 47, 73, 0.94)';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#22d3ee';
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(targetLabel, targetLabelX + targetLabelWidth / 2, targetLabelY + 12);
      }
    }
    const robotPosition = mapToScreen(
      currentRobotStatus?.LocationX,
      currentRobotStatus?.LocationY
    );
    if (showCurrentRobot && robotPosition) {
      const headingValue = currentRobotStatus?.Angle;
      const headingRadians = headingValue === '' || headingValue == null
        ? Number.NaN
        : Number(headingValue);

      ctx.save();
      ctx.shadowColor = 'rgba(239, 68, 68, 0.8)';
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.arc(robotPosition.x, robotPosition.y, 20, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(239, 68, 68, 0.22)';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(robotPosition.x, robotPosition.y, 12, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(robotPosition.x, robotPosition.y, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.restore();

      if (Number.isFinite(headingRadians)) {
        const screenHeading = viewRef.current.rotation - headingRadians;
        const directionX = Math.cos(screenHeading);
        const directionY = Math.sin(screenHeading);
        const perpendicularX = -directionY;
        const perpendicularY = directionX;
        const arrowStart = 13;
        const arrowEnd = 42;
        const arrowHeadLength = 11;
        const arrowHeadWidth = 8;
        const tipX = robotPosition.x + directionX * arrowEnd;
        const tipY = robotPosition.y + directionY * arrowEnd;
        const arrowBaseX = tipX - directionX * arrowHeadLength;
        const arrowBaseY = tipY - directionY * arrowHeadLength;

        ctx.save();
        ctx.shadowColor = 'rgba(15, 23, 42, 0.75)';
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.moveTo(
          robotPosition.x + directionX * arrowStart,
          robotPosition.y + directionY * arrowStart
        );
        ctx.lineTo(arrowBaseX, arrowBaseY);
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 5;
        ctx.lineCap = 'round';
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(
          arrowBaseX + perpendicularX * arrowHeadWidth,
          arrowBaseY + perpendicularY * arrowHeadWidth
        );
        ctx.lineTo(
          arrowBaseX - perpendicularX * arrowHeadWidth,
          arrowBaseY - perpendicularY * arrowHeadWidth
        );
        ctx.closePath();
        ctx.fillStyle = '#fbbf24';
        ctx.fill();
        ctx.restore();
      }

      const robotLabel = currentRobotName || 'Robot';
      ctx.font = '800 13px Arial';
      const robotLabelWidth = ctx.measureText(robotLabel).width + 22;
      const robotLabelX = robotPosition.x - robotLabelWidth / 2;
      const robotLabelY = robotPosition.y - 50;
      drawRoundedRect(ctx, robotLabelX, robotLabelY, robotLabelWidth, 27, 7);
      ctx.fillStyle = 'rgba(127, 29, 29, 0.97)';
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#fbbf24';
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(robotLabel, robotPosition.x, robotLabelY + 13.5);
    }

    hitTargetsRef.current = hitTargets;
  };

  fitViewRef.current = () => {
    const image = imageRef.current;
    const { width, height } = sizeRef.current;
    if (!image || !width || !height) return;

    const padding = 28;
    const fitScale = Math.min(
      (width - padding * 2) / image.naturalWidth,
      (height - padding * 2) / image.naturalHeight
    );
    viewRef.current = {
      scale: fitScale,
      fitScale,
      rotation: 0,
      panX: 0,
      panY: 0,
    };
    drawRef.current();
  };

  useEffect(() => {
    const loadId = imageLoadIdRef.current + 1;
    imageLoadIdRef.current = loadId;
    imageRef.current = null;
    hitTargetsRef.current = [];

    if (!mapImage) {
      drawRef.current();
      return undefined;
    }

    const image = new Image();
    image.onload = () => {
      if (imageLoadIdRef.current !== loadId) return;
      imageRef.current = image;
      fitViewRef.current();
    };
    image.onerror = () => {
      if (imageLoadIdRef.current !== loadId) return;
      imageRef.current = null;
      drawRef.current();
    };
    image.src = mapImage.startsWith('data:')
      ? mapImage
      : `data:image/jpeg;base64,${mapImage}`;

    return () => {
      imageLoadIdRef.current += 1;
    };
  }, [mapImage]);

  useEffect(() => {
    drawRef.current();
  }, [
    waypoints,
    robotStatus,
    laserPoints,
    robotName,
    selectedWaypointName,
    showRobot,
    showWaypoints,
    showAllWaypointNames,
    waypointSearch,
    pointTargetMode,
    temporaryPointTarget,
    routePoints,
    routeState,
  ]);

  useEffect(() => {
    const animateSelectedWaypoint = Boolean(selectedWaypointName && showWaypoints);
    if (!animateSelectedWaypoint && !temporaryPointTarget && routePoints.length < 2) return undefined;

    let animationFrame = null;
    let previousDrawTime = 0;
    const animateSelection = (time) => {
      if (time - previousDrawTime >= 33) {
        previousDrawTime = time;
        drawRef.current();
      }
      animationFrame = window.requestAnimationFrame(animateSelection);
    };

    animationFrame = window.requestAnimationFrame(animateSelection);
    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [selectedWaypointName, showWaypoints, temporaryPointTarget, routePoints]);

  useMountEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return undefined;

    const getCanvasPoint = (event) => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    const findTarget = ({ x, y }) => hitTargetsRef.current.find((target) => {
      const inLabel = target.label && (
        x >= target.label.x
        && x <= target.label.x + target.label.width
        && y >= target.label.y
        && y <= target.label.y + target.label.height
      );
      const markerDistance = Math.hypot(x - target.marker.x, y - target.marker.y);
      return inLabel || markerDistance <= target.marker.radius;
    });

    const getPointTargetHit = (point) => {
      const target = temporaryPointTargetRef.current;
      if (!dataRef.current.pointTargetMode || !target) return null;

      const screen = mapToScreen(target.x, target.y);
      if (!screen) return null;
      const heading = Number.isFinite(Number(target.angle)) ? Number(target.angle) : 0;
      const screenHeading = viewRef.current.rotation - heading;
      const ringRadius = 38;
      const handle = {
        x: screen.x + Math.cos(screenHeading) * ringRadius,
        y: screen.y + Math.sin(screenHeading) * ringRadius,
      };
      const centerDistance = Math.hypot(point.x - screen.x, point.y - screen.y);
      const handleDistance = Math.hypot(point.x - handle.x, point.y - handle.y);

      return {
        center: centerDistance <= 16,
        headingControl: handleDistance <= 14 || Math.abs(centerDistance - ringRadius) <= 8,
      };
    };

    const updatePointTargetHeading = (point) => {
      const target = temporaryPointTargetRef.current;
      const mapPoint = screenToMap(point.x, point.y, false);
      if (!target || !mapPoint) return;

      updateTemporaryPointTarget({
        ...target,
        angle: Math.atan2(mapPoint.y - target.y, mapPoint.x - target.x),
      });
      drawRef.current();
    };

    let resizeFrame = null;
    const resize = () => {
      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const previousSize = sizeRef.current;
      if (
        previousSize.width === width
        && previousSize.height === height
        && previousSize.dpr === dpr
      ) {
        return;
      }

      const firstSize = sizeRef.current.width === 0;
      sizeRef.current = { width, height, dpr };
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      if (firstSize && imageRef.current) {
        fitViewRef.current();
      } else {
        drawRef.current();
      }
    };

    const scheduleResize = () => {
      if (resizeFrame !== null) return;
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = null;
        resize();
      });
    };

    const updateGesture = () => {
      if (pointersRef.current.size !== 2) {
        gestureRef.current = null;
        return;
      }
      const points = [...pointersRef.current.values()];
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      gestureRef.current = {
        distance: Math.max(1, Math.hypot(dx, dy)),
        angle: Math.atan2(dy, dx),
        center: {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        },
        view: { ...viewRef.current },
      };
    };

    const onPointerDown = (event) => {
      const point = getCanvasPoint(event);
      pointersRef.current.set(event.pointerId, point);
      canvas.setPointerCapture(event.pointerId);

      if (pointersRef.current.size === 2) {
        dragRef.current = null;
        updateGesture();
        return;
      }

      const pointTargetHit = getPointTargetHit(point);
      if (!dataRef.current.targetLocked && pointTargetHit?.headingControl && event.button !== 2) {
        updatePointTargetHeading(point);
        dragRef.current = {
          pointerId: event.pointerId,
          start: point,
          mode: 'target-heading',
          moved: true,
        };
        canvas.style.cursor = 'grabbing';
        return;
      }

      if (!dataRef.current.targetLocked && pointTargetHit?.center && event.button !== 2) {
        dragRef.current = {
          pointerId: event.pointerId,
          start: point,
          mode: 'target-move',
          moved: false,
        };
        canvas.style.cursor = 'grab';
        return;
      }

      dragRef.current = {
        pointerId: event.pointerId,
        start: point,
        view: { ...viewRef.current },
        mode: event.button === 2 || event.shiftKey ? 'rotate' : 'pan',
        moved: false,
      };
    };

    const onPointerMove = (event) => {
      const point = getCanvasPoint(event);
      const mapPixel = screenToMap(point.x, point.y);
      setHoveredMapPixel((current) => (
        current?.x === mapPixel?.x && current?.y === mapPixel?.y
          ? current
          : mapPixel
      ));

      if (pointersRef.current.has(event.pointerId)) {
        pointersRef.current.set(event.pointerId, point);
      }

      if (gestureRef.current && pointersRef.current.size === 2) {
        const points = [...pointersRef.current.values()];
        const dx = points[1].x - points[0].x;
        const dy = points[1].y - points[0].y;
        const distance = Math.max(1, Math.hypot(dx, dy));
        const angle = Math.atan2(dy, dx);
        const center = {
          x: (points[0].x + points[1].x) / 2,
          y: (points[0].y + points[1].y) / 2,
        };
        const start = gestureRef.current;
        const nextScale = clamp(
          start.view.scale * (distance / start.distance),
          start.view.fitScale * 0.45,
          start.view.fitScale * 16
        );
        viewRef.current = {
          ...start.view,
          scale: nextScale,
          rotation: start.view.rotation + angle - start.angle,
          panX: start.view.panX + center.x - start.center.x,
          panY: start.view.panY + center.y - start.center.y,
        };
        drawRef.current();
        return;
      }

      const drag = dragRef.current;
      if (drag && drag.pointerId === event.pointerId) {
        if (drag.mode === 'target-heading') {
          updatePointTargetHeading(point);
          canvas.style.cursor = 'grabbing';
          return;
        }

        if (drag.mode === 'target-move') {
          const moveDistance = Math.hypot(
            point.x - drag.start.x,
            point.y - drag.start.y
          );
          drag.moved = drag.moved || moveDistance > 4;
          if (drag.moved) {
            const mapPoint = screenToMap(point.x, point.y);
            const target = temporaryPointTargetRef.current;
            if (mapPoint && target) {
              updateTemporaryPointTarget({ ...target, ...mapPoint });
              drawRef.current();
            }
            canvas.style.cursor = 'grabbing';
          }
          return;
        }

        const dx = point.x - drag.start.x;
        const dy = point.y - drag.start.y;
        drag.moved = drag.moved || Math.hypot(dx, dy) > 4;
        if (drag.mode === 'rotate') {
          viewRef.current.rotation = drag.view.rotation + dx * 0.008;
        } else {
          viewRef.current.panX = drag.view.panX + dx;
          viewRef.current.panY = drag.view.panY + dy;
        }
        drawRef.current();
        return;
      }

      if (dataRef.current.pointTargetMode) {
        const pointTargetHit = getPointTargetHit(point);
        canvas.style.cursor = dataRef.current.targetLocked
          ? 'grab'
          : pointTargetHit?.headingControl
          ? 'grab'
          : (pointTargetHit?.center ? 'pointer' : 'crosshair');
        if (hoveredWaypointRef.current !== null) {
          hoveredWaypointRef.current = null;
          drawRef.current();
        }
        return;
      }

      const target = findTarget(point);
      const nextHovered = target?.key || null;
      if (hoveredWaypointRef.current !== nextHovered) {
        hoveredWaypointRef.current = nextHovered;
        canvas.style.cursor = target ? 'pointer' : 'grab';
        drawRef.current();
      }
    };

    const onPointerLeave = () => {
      if (pointersRef.current.size === 0) {
        setHoveredMapPixel(null);
      }
    };

    const onPointerUp = (event) => {
      const point = getCanvasPoint(event);
      const drag = dragRef.current;
      const isClick = (
        drag
        && drag.pointerId === event.pointerId
        && !drag.moved
        && pointersRef.current.size === 1
        && event.button !== 2
      );

      if (isClick && dataRef.current.pointTargetMode && !dataRef.current.targetLocked) {
        const pointTargetHit = getPointTargetHit(point);
        const existingTarget = temporaryPointTargetRef.current;
        if (
          pointTargetHit?.center
          && existingTarget
          && typeof dataRef.current.onPointTargetClick === 'function'
        ) {
          dataRef.current.onPointTargetClick(existingTarget, clearTemporaryPointTarget);
        } else {
          const mapPoint = screenToMap(point.x, point.y);
          if (mapPoint) {
            updateTemporaryPointTarget({ ...mapPoint, angle: 0 });
            drawRef.current();
          }
        }
      } else if (isClick) {
        const target = findTarget(point);
        if (target && typeof dataRef.current.onWaypointClick === 'function') {
          dataRef.current.onWaypointClick(target.waypoint);
        }
      }

      pointersRef.current.delete(event.pointerId);
      dragRef.current = null;
      updateGesture();
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
    };

    const onWheel = (event) => {
      event.preventDefault();
      const point = getCanvasPoint(event);
      const view = viewRef.current;
      const nextScale = clamp(
        view.scale * Math.exp(-event.deltaY * 0.0012),
        view.fitScale * 0.45,
        view.fitScale * 16
      );
      const ratio = nextScale / view.scale;
      const centeredX = point.x - sizeRef.current.width / 2;
      const centeredY = point.y - sizeRef.current.height / 2;
      view.panX = centeredX - (centeredX - view.panX) * ratio;
      view.panY = centeredY - (centeredY - view.panY) * ratio;
      view.scale = nextScale;
      drawRef.current();
    };

    const onContextMenu = (event) => event.preventDefault();
    const resizeObserver = new ResizeObserver(scheduleResize);
    resizeObserver.observe(container);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerleave', onPointerLeave);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onContextMenu);
    scheduleResize();

    return () => {
      resizeObserver.disconnect();
      if (resizeFrame !== null) {
        window.cancelAnimationFrame(resizeFrame);
      }
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onContextMenu);
    };
  }, []);

  const zoomAtCenter = (factor) => {
    const view = viewRef.current;
    view.scale = clamp(
      view.scale * factor,
      view.fitScale * 0.45,
      view.fitScale * 16
    );
    drawRef.current();
  };

  const rotate = (radians) => {
    viewRef.current.rotation += radians;
    drawRef.current();
  };

  const togglePointTargetMode = () => {
    if (targetLocked) return;
    const nextMode = !pointTargetMode;
    setPointTargetMode(nextMode);
    if (!nextMode) {
      clearTemporaryPointTarget();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          marginBottom: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          Drag to pan · Scroll to zoom · Shift/right-drag or two fingers to tilt
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Zoom in">
            <IconButton size="small" onClick={() => zoomAtCenter(1.25)}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out">
            <IconButton size="small" onClick={() => zoomAtCenter(0.8)}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tilt left">
            <IconButton size="small" onClick={() => rotate(-Math.PI / 12)}>
              <RotateLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tilt right">
            <IconButton size="small" onClick={() => rotate(Math.PI / 12)}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset map">
            <IconButton size="small" onClick={() => fitViewRef.current()}>
              <CenterFocusStrongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          marginBottom: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          size="small"
          variant={pointTargetMode ? 'contained' : 'outlined'}
          color="secondary"
          startIcon={<AddLocationAltIcon />}
          onClick={togglePointTargetMode}
          disabled={targetLocked}
        >
          {targetLocked ? missionLockLabel : pointTargetMode ? 'Point mode on' : 'Send to point'}
        </Button>
        {temporaryPointTarget && !targetLocked && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={clearTemporaryPointTarget}
          >
            Clear target
          </Button>
        )}
        <TextField
          size="small"
          label="Search waypoints"
          value={waypointSearch}
          onChange={(event) => setWaypointSearch(event.target.value)}
          disabled={!showWaypoints}
          sx={{ width: { xs: '100%', sm: 210 } }}
          inputProps={{ 'aria-label': 'Filter waypoints by name' }}
        />
        <FormControlLabel
          sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
          control={(
            <Switch
              size="small"
              checked={showRobot}
              onChange={(event) => setShowRobot(event.target.checked)}
            />
          )}
          label="Robot"
        />
        <FormControlLabel
          sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
          control={(
            <Switch
              size="small"
              checked={showWaypoints}
              onChange={(event) => setShowWaypoints(event.target.checked)}
            />
          )}
          label="Waypoints"
        />
        <FormControlLabel
          sx={{ margin: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
          control={(
            <Switch
              size="small"
              checked={showAllWaypointNames}
              onChange={(event) => setShowAllWaypointNames(event.target.checked)}
              disabled={!showWaypoints}
            />
          )}
          label="Waypoint names"
        />
        {pointTargetMode && (
          <Typography sx={{ color: 'secondary.main', fontSize: '0.75rem', fontWeight: 600 }}>
            Drag the centre to move, drag the ring to set heading, or click the centre to continue
          </Typography>
        )}
      </Box>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: '380px', md: '520px' },
          overflow: 'hidden',
          borderRadius: '8px',
          border: '2px solid',
          borderColor: 'divider',
          backgroundColor: '#111827',
        }}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label="Interactive robot location map with clickable waypoints"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            cursor: 'grab',
            touchAction: 'none',
          }}
        />
        {hoveredMapPixel && (
          <Box
            sx={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              backgroundColor: 'rgba(15, 23, 42, 0.88)',
              color: '#ffffff',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              pointerEvents: 'none',
            }}
          >
            Map pixel: X {hoveredMapPixel.x}, Y {hoveredMapPixel.y}
          </Box>
        )}
      </Box>
    </Box>
  );
}
